import { RuntimeConfig } from "@/types/runtime-config";
import { useEffect, useState } from "react";

// In-memory cache to avoid repeated API calls
let cachedConfig: RuntimeConfig | null = null;
let configPromise: Promise<RuntimeConfig> | null = null;

async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing promise if fetch is in progress
  if (configPromise) {
    return configPromise;
  }

  // Start new fetch
  configPromise = fetch("/api/runtime-config")
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch runtime config: ${res.statusText}`);
      }
      return res.json();
    })
    .then((config: RuntimeConfig) => {
      cachedConfig = config;
      configPromise = null;
      return config;
    })
    .catch((error) => {
      console.error("Error fetching runtime config:", error);
      configPromise = null;
      // Return default config on error
      return getDefaultConfig();
    });

  return configPromise;
}

function getDefaultConfig(): RuntimeConfig {
  return {
    googleMapsKey: "AIzaSyDCoYgKMvU9_kpGutWJEcr74vLFUMv5N88",
    googleAutocompleteKey: "AIzaSyABX4LTqTLQGg_b3jFOH8Z6_H5CDqn8tbc",
    googleGeocodeKey: "AIzaSyDCoYgKMvU9_kpGutWJEcr74vLFUMv5N88",
    googleRecaptchaKey: "AIzaSyDCoYgKMvU9_kpGutWJEcr74vLFUMv5N88",
    algoliaAppId: "",
    algoliaApiKey: "",
    runBuggy: {
      prod: { clientId: "", scriptUrl: "" },
      dev: { clientId: "", scriptUrl: "" },
    },
    airbrake: { projectId: 0, projectKey: "" },
    cognito: {
      userPool: "",
      clientId: "",
      identityPool: "",
    },
    awsRegion: "us-east-2",
    posthog: {
      key: "",
      host: "/ingest",
    },
    pricepoint: { url: "", accessKey: "" },
    apiImageUrl: "",
    prefixPrelogin: false,
    buildId: "n/a",
  };
}

/**
 * Hook to access runtime configuration
 * Fetches config once on mount and caches it
 */
export function useRuntimeConfig() {
  const [config, setConfig] = useState<RuntimeConfig | null>(cachedConfig);
  const [loading, setLoading] = useState(!cachedConfig);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (cachedConfig) {
      return; // Already loaded
    }

    setLoading(true);
    fetchRuntimeConfig()
      .then((cfg) => {
        setConfig(cfg);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setConfig(getDefaultConfig());
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return { config, loading, error };
}

/**
 * Preload runtime config (call this in _app.tsx)
 * This ensures config is loaded before components need it
 */
export function preloadRuntimeConfig() {
  if (typeof window !== "undefined" && !cachedConfig && !configPromise) {
    fetchRuntimeConfig();
  }
}
